import React, { useState, useEffect } from 'react';
import { Routes, Route, Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import DistrictsList from './superadmin/DistrictsList';
import AddDistrict from './superadmin/AddDistrict';
import EditDistrict from './superadmin/EditDistrict';
import SuperAdminsList from './superadmin/SuperAdminsList';
import AddSuperAdmin from './superadmin/AddSuperAdmin';
import MasterFormsList from './superadmin/MasterFormsList';
import AddMasterForm from './superadmin/AddMasterForm';
import FormBuilder from './superadmin/FormBuilder';
import ViewMasterForm from './superadmin/ViewMasterForm';
import EditMasterForm from './superadmin/EditMasterForm';
import FormInstancesList from './superadmin/FormInstancesList';
import CreateFormInstance from './superadmin/CreateFormInstance';
import DistrictFormInstancesList from './superadmin/DistrictFormInstancesList';
import DistrictFormInstances from './superadmin/DistrictFormInstances';
import DistrictAdminsList from './superadmin/DistrictAdminsList';
import AddDistrictAdmin from './superadmin/AddDistrictAdmin';
import TrashedFormsList from './superadmin/TrashedFormsList';
import BackupDatabase from './superadmin/BackupDatabase';
import DistrictAuditLogs from './superadmin/DistrictAuditLogs';
import './SuperAdminDashboard.css';

const SuperAdminDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Close mobile menu when route changes
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileMenuOpen]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
  };

  return (
    <div className="dashboard-container">
      <header className="dashboard-header">
        <div className="dashboard-header-content">
          <button 
            className="mobile-menu-toggle"
            onClick={toggleMobileMenu}
            aria-label="Toggle menu"
            aria-expanded={mobileMenuOpen}
          >
            <span></span>
            <span></span>
            <span></span>
          </button>
          <h1>Back to School Forms Admin</h1>
        </div>
        <div className="header-actions">
          <span className="welcome-text">Welcome, {user?.username}</span>
          <button onClick={handleLogout} className="btn-logout">Logout</button>
        </div>
      </header>
      {mobileMenuOpen && (
        <div className="mobile-menu-backdrop" onClick={closeMobileMenu}></div>
      )}
      <div className="dashboard-content">
        <nav className={`dashboard-sidebar ${mobileMenuOpen ? 'mobile-open' : ''}`}>
          <div className="sidebar-header">
            <h2>Menu</h2>
            <button 
              className="mobile-menu-close"
              onClick={closeMobileMenu}
              aria-label="Close menu"
            >
              ×
            </button>
          </div>
          <ul>
            <li>
              <Link to="/superadmin/districts" onClick={closeMobileMenu}>Districts</Link>
              <ul className="submenu">
                <li><Link to="/superadmin/districts/add" onClick={closeMobileMenu}>Add District</Link></li>
                <li><Link to="/superadmin/districts" onClick={closeMobileMenu}>List Districts</Link></li>
              </ul>
            </li>
            <li>
              <Link to="/superadmin/users" onClick={closeMobileMenu}>Users</Link>
              <ul className="submenu">
                <li><Link to="/superadmin/users/superadmins/add" onClick={closeMobileMenu}>Add Superadmin</Link></li>
                <li><Link to="/superadmin/users/superadmins" onClick={closeMobileMenu}>List Superadmins</Link></li>
              </ul>
            </li>
            <li>
              <Link to="/superadmin/forms" onClick={closeMobileMenu}>Forms</Link>
              <ul className="submenu">
                <li><Link to="/superadmin/forms/add" onClick={closeMobileMenu}>Add Master Form</Link></li>
                <li><Link to="/superadmin/forms" onClick={closeMobileMenu}>List Master Forms</Link></li>
                <li><Link to="/superadmin/form-instances" onClick={closeMobileMenu}>Form Instances</Link></li>
                <li><Link to="/superadmin/trashed-forms" onClick={closeMobileMenu}>Trashed Forms</Link></li>
              </ul>
            </li>
            <li>
              <Link to="/superadmin/backups" onClick={closeMobileMenu}>Database Backups</Link>
            </li>
            <li>
              <Link to="/superadmin/audit-logs" onClick={closeMobileMenu}>District Logs</Link>
            </li>
          </ul>
        </nav>
        <main className="dashboard-main">
          <Routes>
            <Route path="/" element={<div><h2>Welcome to Super Admin Dashboard</h2></div>} />
            <Route path="/districts" element={<DistrictsList />} />
            <Route path="/districts/add" element={<AddDistrict />} />
            <Route path="/districts/edit/:id" element={<EditDistrict />} />
            <Route path="/users/superadmins" element={<SuperAdminsList />} />
            <Route path="/users/superadmins/add" element={<AddSuperAdmin />} />
            <Route path="/forms" element={<MasterFormsList />} />
            <Route path="/forms/add" element={<FormBuilder />} />
            <Route path="/forms/view/:id" element={<ViewMasterForm />} />
            <Route path="/forms/edit/:id" element={<EditMasterForm />} />
            <Route path="/form-instances" element={<FormInstancesList />} />
            <Route path="/form-instances/create" element={<CreateFormInstance />} />
            <Route path="/form-instances/create/:masterFormId" element={<CreateFormInstance />} />
            <Route path="/form-instances/:subdomain/create" element={<CreateFormInstance />} />
            <Route path="/form-instances/:subdomain" element={<DistrictFormInstancesList />} />
            <Route path="/trashed-forms" element={<TrashedFormsList />} />
            <Route path="/districts/:districtId/form-instances" element={<DistrictFormInstances />} />
            <Route path="/districts/:districtId/admins" element={<DistrictAdminsList />} />
            <Route path="/districts/:districtId/admins/add" element={<AddDistrictAdmin />} />
            <Route path="/backups" element={<BackupDatabase />} />
            <Route path="/audit-logs" element={<DistrictAuditLogs />} />
            <Route path="/audit-logs/:districtId" element={<DistrictAuditLogs />} />
          </Routes>
        </main>
      </div>
    </div>
  );
};

export default SuperAdminDashboard;

